import {
  create,
  getOption,
  isMessage,
  ScalarType,
  type DescField,
  type DescMethod,
  type Message,
} from '@bufbuild/protobuf';
// oxlint-disable-next-line no-restricted-imports
import { createConnectQueryKey } from '@connectrpc/connect-query';
import {
  account_id_expression,
  permissions,
  requires_all_permissions,
} from '@qdrant/qdrant-cloud-public-api/common/v1';
import {
  type ListEffectivePermissionsResponse,
  ListEffectivePermissionsResponseSchema,
} from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { listEffectivePermissions } from '@qdrant/qdrant-cloud-public-api/iam/v1/connect-query';
import { type QueryClient } from '@tanstack/react-query';
import { AccessControlError, withSentryContext } from '@/api/errors';
import { type NonEmptyArray } from '@/utils/types';

/**
 * The permissions no RPC in the public API declares, so no `DescMethod` can stand in for them and a
 * literal string is the only way to check one. Everything else is reachable through some method's
 * `permissions` proto option and must be checked from there instead — see `checkRpcPermissions`.
 *
 * Why each one is here:
 *
 * - `read:cluster_data` — the database enforces it. Reaching cluster data means a direct
 *   browser-to-cluster request, which never passes through the control plane.
 * - `read:profile`, `read:user`, `write:user` — the RPCs that would carry them (`getUserProfile`,
 *   `updateUserProfile`, `updateUser`, `deleteUser`) are all declared public: they act on the caller's
 *   own user, so the gateway checks no account permission. Roles still grant the three, so they are
 *   real permission names a user can hold, not dead strings.
 * - `read:serverless_data`, `write:serverless_data` — serverless-backend's `CreateEphemeralDashboardToken`
 *   checks them and maps them to the token's access rule; the gateway requires only `read:serverless_spaces`
 *   of that RPC. The token reaches a space's collections by a direct browser-to-space request.
 *
 * Kept in sync by `permissions.invariant.test.ts`, which fails if any of these gains an RPC.
 */
export const PERMISSIONS_WITHOUT_RPC = [
  'read:cluster_data',
  'read:profile',
  'read:user',
  'write:user',
  'read:serverless_data',
  'write:serverless_data',
] as const;

/** A permission that can only be checked as a string, because no RPC declares it. */
export type PermissionWithoutRpc = (typeof PERMISSIONS_WITHOUT_RPC)[number];

/**
 * The string-side counterpart of `checkRpcPermissions`, and the only sanctioned way to check one of
 * the permissions no RPC declares. The `PermissionWithoutRpc` parameter is the whole enforcement:
 * anything reachable through an RPC is a type error here, so it has to be checked from that method's
 * proto options instead.
 *
 * Components hold the permission set already (`useEffectivePermissionsForAccountId`); outside React,
 * `resolveAccountHasPermissionsWithoutRpc` fetches it first and then calls this.
 */
export function hasPermissionsWithoutRpc(
  requiredPermissions: NonEmptyArray<PermissionWithoutRpc>,
  currentPermissions: ReadonlySet<string>,
): boolean {
  return requiredPermissions.every((permission) => currentPermissions.has(permission));
}

/**
 * Extracts the required permissions and allOf/anyOf requirement from a protobuf method descriptor.
 */
export function getMethodPermissionOptions(method: DescMethod) {
  return {
    // The proto uses an empty string "" as a sentinel in the permissions option for public methods (like `getAuthenticatedUser`),
    // so getOption returns `[""]` not `undefined` or `[]`.
    requiredPermissions: getOption(method, permissions).filter(Boolean),
    // Methods that require all permissions set `requires_all_permissions` to true in their proto options.
    // The default (false) is that any one permission suffices.
    requiresAll: getOption(method, requires_all_permissions),
  };
}

/**
 * Methods declare where the account id lives in their request via the `account_id_expression` method
 * option — a dotted path of proto field names (e.g. `cluster.account_id`, `account.id`). Methods that
 * omit it follow the default top-level `account_id` convention.
 */
const DEFAULT_ACCOUNT_ID_PATH = 'account_id';

function getAccountIdExpression(method: DescMethod): string {
  return getOption(method, account_id_expression) || DEFAULT_ACCOUNT_ID_PATH;
}

/**
 * Resolves a method's account-id path to the chain of field descriptors it traverses, or `undefined`
 * when the path does not map to a string field on the request message. Shared by the permissions
 * interceptor (to read the value at runtime) and the invariant test (to assert every gated method
 * exposes a resolvable account scope).
 */
export function resolveAccountIdFieldPath(method: DescMethod): DescField[] | void {
  const segments = getAccountIdExpression(method).split('.');
  const path: DescField[] = [];
  let message = method.input;
  for (const segment of segments) {
    const field = message.fields.find((f) => f.name === segment);
    if (!field) {
      return;
    }
    path.push(field);
    if (field.fieldKind === 'message') {
      message = field.message;
    }
  }
  const leaf = path.at(-1);
  if (leaf?.fieldKind !== 'scalar' || leaf.scalar !== ScalarType.STRING) {
    return;
  }
  return path;
}

/** A protobuf message seen as the properties `resolveAccountId` reads off it by name. */
type MessageFields = Message & Record<string, Message | string | undefined>;

/**
 * Reads the account id from a request message using proto metadata, following the method's
 * `account_id_expression`. Returns nothing when the scope cannot be resolved (the caller should
 * then defer to the server, which is the real authority).
 */
// A Connect interceptor hands the request over as `AsyncIterable<Message> | Message`; `isMessage` parses it.
// oxlint-disable-next-line eslint-js/no-restricted-syntax
export function resolveAccountId(method: DescMethod, input: unknown): string | void {
  const path = resolveAccountIdFieldPath(method);
  if (!(path && isMessage(input))) {
    return;
  }

  // The path comes from the method's options, so each step is read by name rather than off a static
  // type. A step is either the id itself or the message nesting it; anything else is not resolvable.
  let current = input as MessageFields;
  for (const field of path) {
    if (!Object.hasOwn(current, field.localName)) {
      return;
    }
    const value = current[field.localName];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
    if (!isMessage(value)) {
      return;
    }
    current = value as MessageFields;
  }
}

/**
 * Returns the list of permissions that are missing from `currentPermissions`.
 * An empty result means the check passes.
 */
function getMissingPermissions(method: DescMethod, currentPermissions: ReadonlySet<string>): string[] {
  const { requiredPermissions, requiresAll } = getMethodPermissionOptions(method);
  if (requiresAll) {
    return requiredPermissions.filter((p) => !currentPermissions.has(p));
  }
  return requiredPermissions.some((p) => currentPermissions.has(p)) ? [] : [...requiredPermissions];
}

/**
 * Reads an account's effective permissions straight from the QueryCache — the low-level cache instance
 * rather than a query, so the check always sees the most up to date data.
 *
 * Throws when the permissions are not in cache, which means the `$accountId` route loader responsible
 * for loading them has not run. Notice this is different from permissions being loaded but empty (a user
 * with no permissions), which is a valid scenario and does not throw. Callers that may legitimately run
 * before the loader should fetch instead — see `resolveAccountHasRpcPermissions`.
 */
export function getCachedAccountPermissions(queryClient: QueryClient, accountId: string): Set<string> {
  const cacheData = queryClient.getQueryCache().find<ListEffectivePermissionsResponse>({
    exact: false,
    queryKey: createConnectQueryKey({
      schema: listEffectivePermissions,
      cardinality: 'finite',
      input: { accountId },
    }),
  })?.state.data;

  if (cacheData === undefined) {
    throw new Error(
      `getCachedAccountPermissions(): permissions not loaded for account '${accountId}'. ` +
        'Ensure the $accountId route loader has run before making this call.',
    );
  }

  const accountPermissions = create(ListEffectivePermissionsResponseSchema, cacheData).permissions;

  return new Set(accountPermissions.map(({ value }) => value));
}

/**
 * How several methods combine: `allOf` requires every method to pass, `anyOf` requires at least one.
 * Within a single method, allOf/anyOf comes from its own `requires_all_permissions` proto option.
 */
export type PermissionsCombinator = 'allOf' | 'anyOf';

/**
 * Where the permissions to check against come from: either the caller already has them at hand (hooks
 * reading them from route context), or it hands over a `queryClient` + account scope and lets the check
 * pull them out of the cache — which throws if they were never loaded (see `getCachedAccountPermissions`).
 */
export type RpcPermissionsSource = ReadonlySet<string> | { queryClient: QueryClient; accountId: string };

/**
 * Single control point for "may the user call these RPCs?". Works in the Connect interceptor, in
 * components/hooks (`useRpcPermissions`) and in route loaders (`resolveAccountHasRpcPermissions`) alike.
 *
 * `missingPermissions` is empty when the check passes, and otherwise carries the union of what each
 * failing method was missing — which is what `AccessControlError` reports to the user.
 */
export function checkRpcPermissions(
  methods: NonEmptyArray<DescMethod>,
  source: RpcPermissionsSource,
  combinator: PermissionsCombinator = 'allOf',
): { matches: boolean; missingPermissions: string[] } {
  const currentPermissions =
    'queryClient' in source ? getCachedAccountPermissions(source.queryClient, source.accountId) : source;
  const missingPerMethod = methods.map((method) => getMissingPermissions(method, currentPermissions));
  const passed = missingPerMethod.filter((missing) => missing.length === 0).length;
  const matches = combinator === 'allOf' ? passed === methods.length : passed > 0;

  return { matches, missingPermissions: matches ? [] : [...new Set(missingPerMethod.flat())] };
}

/**
 * The `AccessControlError` that renders the Access Denied screen, carrying what was missing.
 *
 * `beforeLoad` never reaches the QueryClient's `onError` pipeline, so the demotion that pipeline applies
 * to an `AccessControlError` (`level: 'warning'` — an expected product state, not a defect) is attached
 * here instead, along with the missing permissions as Sentry extra. Both paths that can refuse an RPC
 * then report identically.
 */
const accessDenied = (missingPermissions: string[]) =>
  withSentryContext(new AccessControlError(missingPermissions), {
    level: 'warning',
    extra: { missing_permissions: missingPermissions },
  });

/** The check plus the refusal, for callers that gate rather than branch: throws `accessDenied`. */
export function assertRpcPermissions(
  methods: NonEmptyArray<DescMethod>,
  source: RpcPermissionsSource,
  combinator?: PermissionsCombinator,
): void {
  const { missingPermissions } = checkRpcPermissions(methods, source, combinator);
  if (missingPermissions.length === 0) {
    return;
  }

  throw accessDenied(missingPermissions);
}

/**
 * `assertRpcPermissions` for the permissions no RPC declares, which the permissions interceptor therefore never
 * refuses. Reads them from the cache, which `$accountId` fills before any child route loads.
 */
export function assertPermissionsWithoutRpc(
  requiredPermissions: NonEmptyArray<PermissionWithoutRpc>,
  { queryClient, accountId }: { queryClient: QueryClient; accountId: string },
): void {
  const currentPermissions = getCachedAccountPermissions(queryClient, accountId);
  const missingPermissions = requiredPermissions.filter((permission) => !currentPermissions.has(permission));
  if (missingPermissions.length === 0) {
    return;
  }

  throw accessDenied(missingPermissions);
}
